import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import { BE_API_AUTH_URL } from '../constants/app.constants';
import { AuthService } from './auth.service';
import { BillingDocumentsResponse } from '../models/billing-document.models';
import {
  SpecialOfferRequestPayload,
  SpecialOfferRequestResponse,
  UpgradeContractDraftPayload,
  UpgradeContractDraftResponse,
  UpgradeContractPreviewResponse,
  UpgradeSettingsResponse,
  UpgradeContractSignPayload,
  UpgradeContractSignResponse,
} from '../models/upgrade-contract.models';

@Injectable({
  providedIn: 'root',
})
export class BillingService {
  constructor(private http: HttpClient, private authService: AuthService) {}

  getMyBillingDocuments(): Observable<BillingDocumentsResponse> {
    return this.http
      .get<BillingDocumentsResponse>(`${BE_API_AUTH_URL}/billing/documents`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getMyBillingDocumentDownloadUrl(documentId: string): Observable<{ url: string }> {
    return this.http
      .get<{ url: string }>(`${BE_API_AUTH_URL}/billing/documents/${documentId}/download`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  getUpgradeSettings(): Observable<UpgradeSettingsResponse> {
    return this.http
      .get<UpgradeSettingsResponse>(`${BE_API_AUTH_URL}/billing/upgrade-settings`, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  requestSpecialOffer(payload: SpecialOfferRequestPayload): Observable<SpecialOfferRequestResponse> {
    return this.http
      .post<SpecialOfferRequestResponse>(`${BE_API_AUTH_URL}/billing/special-offer-request`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  increaseMyPropertyLimit(newMaxProperties: number): Observable<{
    message: string;
    company: { _id: string; maxProperties: number; plan: string };
    usedProperties: number;
    remainingProperties: number;
  }> {
    return this.http
      .post<{
        message: string;
        company: { _id: string; maxProperties: number; plan: string };
        usedProperties: number;
        remainingProperties: number;
      }>(`${BE_API_AUTH_URL}/billing/increase-properties`, { newMaxProperties }, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  increaseMySeatLimit(newSeatLimit: number): Observable<{
    message: string;
    company: { _id: string; seatLimit: number; plan: string };
    seatInfo: {
      seatsUsed: number;
      effectiveSeatLimit: number | null;
      seatLimit: number | null;
      seatEnforcementEnabled: boolean;
      plan: string;
    };
  }> {
    return this.http
      .post<{
        message: string;
        company: { _id: string; seatLimit: number; plan: string };
        seatInfo: {
          seatsUsed: number;
          effectiveSeatLimit: number | null;
          seatLimit: number | null;
          seatEnforcementEnabled: boolean;
          plan: string;
        };
      }>(`${BE_API_AUTH_URL}/billing/increase-seats`, { newSeatLimit }, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  createUpgradeContractDraft(payload: UpgradeContractDraftPayload): Observable<UpgradeContractDraftResponse> {
    return this.http
      .post<UpgradeContractDraftResponse>(`${BE_API_AUTH_URL}/billing/upgrade-contracts`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  previewUpgradeContract(snapshotId: string): Observable<UpgradeContractPreviewResponse> {
    return this.http
      .get(`${BE_API_AUTH_URL}/billing/upgrade-contracts/${snapshotId}/preview`, {
        headers: this.authService.createAuthorizationHeader(),
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => ({
          blob: response.body || new Blob([], { type: 'application/pdf' }),
          filename: this.resolveContractFilename(response.headers),
        })),
        catchError(this.authService.handleError.bind(this.authService))
      );
  }

  signUpgradeContract(snapshotId: string, payload: UpgradeContractSignPayload): Observable<UpgradeContractSignResponse> {
    return this.http
      .post<UpgradeContractSignResponse>(`${BE_API_AUTH_URL}/billing/upgrade-contracts/${snapshotId}/sign`, payload, {
        headers: this.authService.createAuthorizationHeader(),
      })
      .pipe(catchError(this.authService.handleError.bind(this.authService)));
  }

  private resolveContractFilename(headers: HttpResponse<Blob>['headers']): string {
    const explicitFilename = headers.get('X-Contract-Filename');
    if (explicitFilename && explicitFilename.trim()) {
      return explicitFilename.trim();
    }

    const contentDisposition = headers.get('Content-Disposition') || '';
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]).trim();
    }

    const fallbackMatch = contentDisposition.match(/filename="([^"]+)"/i);
    if (fallbackMatch?.[1]) {
      return fallbackMatch[1].trim();
    }

    return 'Dienstleistungsvertrag_Scanafy_Vertragsvorschau.pdf';
  }
}
